import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Badge } from "../components/ui/badge";
import { 
  ArrowLeft, ArrowUpRight, ArrowDownLeft, Shield, 
  Filter, ChevronLeft, ChevronRight, ExternalLink
} from "lucide-react";
import { motion } from "framer-motion";
import { API } from "../App";

const CHAIN_CONFIG = {
  xrp: { name: "XRP", color: "#23292F", icon: "🌊" },
  sol: { name: "Solana", color: "#9945FF", icon: "☀️" },
  xlm: { name: "Stellar", color: "#14B8E0", icon: "⭐" },
  hbar: { name: "HBAR", color: "#3A3A3A", icon: "⬡" },
  btc: { name: "Bitcoin", color: "#F7931A", icon: "₿" },
  eth: { name: "Ethereum", color: "#627EEA", icon: "⟠" },
  link: { name: "Chainlink", color: "#375BD2", icon: "⬡" }
};

const TransactionHistory = () => {
  const navigate = useNavigate();
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    chain: "all",
    tx_type: "all"
  });
  const [pagination, setPagination] = useState({
    skip: 0,
    limit: 10,
    total: 0,
    hasMore: false
  });

  const getAuthConfig = () => {
    const token = localStorage.getItem('auth_token');
    return token ? { headers: { Authorization: `Bearer ${token}` } } : {};
  };

  const fetchTransactions = async (skip = 0) => {
    setLoading(true);
    try {
      const config = getAuthConfig();
      const params = new URLSearchParams();
      params.append('skip', skip.toString());
      params.append('limit', pagination.limit.toString());
      
      if (filters.chain !== 'all') params.append('chain', filters.chain);
      if (filters.tx_type !== 'all') params.append('tx_type', filters.tx_type);
      
      const response = await axios.get(`${API}/transactions?${params}`, config);
      setTransactions(response.data.transactions || []);
      setPagination(prev => ({
        ...prev,
        skip: skip,
        total: response.data.total,
        hasMore: response.data.has_more
      }));
    } catch (error) {
      console.error("Failed to fetch transactions:", error);
      if (error.response?.status === 401) {
        navigate('/login');
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTransactions(0);
  }, [filters]);

  const handleNextPage = () => {
    const newSkip = pagination.skip + pagination.limit;
    fetchTransactions(newSkip);
  };

  const handlePrevPage = () => {
    const newSkip = Math.max(0, pagination.skip - pagination.limit);
    fetchTransactions(newSkip);
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const truncateAddress = (address) => {
    if (!address) return '';
    return `${address.slice(0, 8)}...${address.slice(-6)}`;
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#020617] via-[#000A1F] to-[#001133]">
      {/* Header */}
      <header className="sticky top-0 z-50 border-b border-white/5 bg-[#020617]/80 backdrop-blur-xl">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button
                variant="ghost"
                size="icon"
                onClick={() => navigate('/dashboard')}
                className="text-white/60 hover:text-white"
                data-testid="back-to-dashboard-btn"
              >
                <ArrowLeft className="w-5 h-5" />
              </Button>
              <h1 className="text-xl font-bold text-white font-['Syne']">Transaction History</h1>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Filters */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-6"
        >
          <Card className="glass-card">
            <CardContent className="p-4">
              <div className="flex items-center gap-4 flex-wrap">
                <div className="flex items-center gap-2">
                  <Filter className="w-4 h-4 text-white/40" />
                  <span className="text-white/60 text-sm">Filters:</span>
                </div>
                
                <Select
                  value={filters.chain}
                  onValueChange={(value) => setFilters(prev => ({ ...prev, chain: value }))}
                >
                  <SelectTrigger className="w-40 bg-[#000A1F] border-[#FFD700]/20 text-white" data-testid="chain-filter">
                    <SelectValue placeholder="All Chains" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#0B1221] border-[#FFD700]/20">
                    <SelectItem value="all" className="text-white">All Chains</SelectItem>
                    {Object.entries(CHAIN_CONFIG).map(([key, config]) => (
                      <SelectItem key={key} value={key} className="text-white">
                        {config.icon} {config.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                <Select
                  value={filters.tx_type}
                  onValueChange={(value) => setFilters(prev => ({ ...prev, tx_type: value }))}
                >
                  <SelectTrigger className="w-40 bg-[#000A1F] border-[#FFD700]/20 text-white" data-testid="type-filter">
                    <SelectValue placeholder="All Types" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#0B1221] border-[#FFD700]/20">
                    <SelectItem value="all" className="text-white">All Types</SelectItem>
                    <SelectItem value="send" className="text-white">Send</SelectItem>
                    <SelectItem value="receive" className="text-white">Receive</SelectItem>
                  </SelectContent>
                </Select>

                <div className="ml-auto text-white/40 text-sm">
                  {pagination.total} transactions
                </div>
              </div>
            </CardContent>
          </Card>
        </motion.div>

        {/* Transactions List */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
        >
          <Card className="glass-card">
            <CardHeader>
              <CardTitle className="text-white/70 text-sm uppercase tracking-wider">
                Recent Transactions
              </CardTitle>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="flex items-center justify-center py-12">
                  <div className="w-8 h-8 border-2 border-[#FFD700] border-t-transparent rounded-full animate-spin" />
                </div>
              ) : transactions.length === 0 ? (
                <div className="text-center py-12">
                  <p className="text-white/40">No transactions found</p>
                  <p className="text-white/20 text-sm mt-2">
                    Transactions will appear here after you send or receive funds
                  </p>
                </div>
              ) : (
                <div className="space-y-3">
                  {transactions.map((tx, index) => (
                    <motion.div
                      key={tx.tx_id}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: index * 0.05 }}
                      className="flex items-center justify-between p-4 rounded-xl bg-white/5 hover:bg-white/10 transition-colors"
                      data-testid={`transaction-${tx.tx_id}`}
                    >
                      <div className="flex items-center gap-4">
                        {/* Icon */}
                        <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                          tx.tx_type === 'send' ? 'bg-red-500/20' : 'bg-green-500/20'
                        }`}>
                          {tx.tx_type === 'send' ? (
                            <ArrowUpRight className="w-5 h-5 text-red-400" />
                          ) : (
                            <ArrowDownLeft className="w-5 h-5 text-green-400" />
                          )}
                        </div>
                        
                        {/* Details */}
                        <div>
                          <div className="flex items-center gap-2">
                            <span className="text-white font-medium capitalize">{tx.tx_type}</span>
                            <Badge variant="outline" className="text-xs border-white/20 text-white/60">
                              {CHAIN_CONFIG[tx.chain]?.icon} {CHAIN_CONFIG[tx.chain]?.name}
                            </Badge>
                            {tx.quantum_verified && (
                              <div className="flex items-center gap-1 text-[#00FFFF]">
                                <Shield className="w-3 h-3" />
                                <span className="text-xs">Verified</span>
                              </div>
                            )}
                          </div>
                          <div className="text-white/40 text-sm mt-1">
                            {tx.tx_type === 'send' ? 'To: ' : 'From: '}
                            <span className="font-mono">
                              {truncateAddress(tx.tx_type === 'send' ? tx.to_address : tx.from_address)}
                            </span>
                          </div>
                        </div>
                      </div>

                      {/* Amount & Status */}
                      <div className="text-right">
                        <p className={`font-semibold ${tx.tx_type === 'send' ? 'text-red-400' : 'text-green-400'}`}>
                          {tx.tx_type === 'send' ? '-' : '+'}{tx.amount} {CHAIN_CONFIG[tx.chain]?.name}
                        </p>
                        <div className="flex items-center justify-end gap-2 mt-1">
                          <Badge 
                            variant="outline" 
                            className={`text-xs ${
                              tx.status === 'completed' 
                                ? 'border-green-500/30 text-green-400' 
                                : tx.status === 'pending'
                                ? 'border-yellow-500/30 text-yellow-400'
                                : 'border-red-500/30 text-red-400'
                            }`}
                          >
                            {tx.status}
                          </Badge>
                          <span className="text-white/30 text-xs">{formatDate(tx.created_at)}</span>
                        </div>
                      </div>
                    </motion.div>
                  ))}
                </div>
              )}

              {/* Pagination */}
              {!loading && transactions.length > 0 && (
                <div className="flex items-center justify-between mt-6 pt-4 border-t border-white/5">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handlePrevPage}
                    disabled={pagination.skip === 0}
                    className="border-white/20 text-white/60 disabled:opacity-30"
                    data-testid="prev-page-btn"
                  >
                    <ChevronLeft className="w-4 h-4 mr-1" />
                    Previous
                  </Button>
                  
                  <span className="text-white/40 text-sm">
                    Showing {pagination.skip + 1} - {Math.min(pagination.skip + pagination.limit, pagination.total)} of {pagination.total}
                  </span>
                  
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleNextPage}
                    disabled={!pagination.hasMore}
                    className="border-white/20 text-white/60 disabled:opacity-30"
                    data-testid="next-page-btn"
                  >
                    Next
                    <ChevronRight className="w-4 h-4 ml-1" />
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        </motion.div>
      </main>
    </div>
  );
};

export default TransactionHistory;
